import { ERROR_SIGNALING_OFFER_BLOCKED_BY_PEER, ERROR_SIGNALING_OFFER_FORBIDDEN, ERROR_SIGNALING_OFFER_PRIVACY_PROTECTION } from "../constants/errors";
import { SIGNALING_ACCEPT, SIGNALING_CANDIDATE, SIGNALING_LEAVE, SIGNALING_OFFER, SIGNALING_RINGING } from "../constants/methods";
import { SIGNALING_STATUS } from "../constants/signaling";
import { RegisteredUser } from "../proto/Global_pb";
import { SignalingGetConfigurationResponse } from "../proto/SignalingGetConfiguration_pb";
import { SignalingOffer, SignalingOfferResponse } from "../proto/SignalingOffer_pb";
import store from "../store";
import { setPartnerStream, setSignalingSessionHold, setSignalingStatus, setUserStream, stopPartnerStream, stopUserStream } from "../store/signaling/actions";
import { SignalingStateType } from "../store/signaling/types";
import ServerError from "./Error/ServerError";
import { protoHandler } from "./proto";

export class WebRTC {
  user?: RegisteredUser.AsObject;
  signalType?: SignalingOffer.AsObject["type"] = SignalingOffer.Type.VIDEO_CALLING;
  callerUserId?: RegisteredUser.AsObject["id"];
  private config?: RTCConfiguration;
  private callerSdp?: string;
  private peerConnection?: RTCPeerConnection;
  private sdp?: RTCSessionDescriptionInit;

  private constructor() {}
  private static _instance: WebRTC;
  static get instance(): WebRTC {
    if (!WebRTC._instance) {
      WebRTC._instance = new WebRTC();
    }
    return WebRTC._instance;
  }

  /**
   * to access user cameras and microphone. it return a promise which show a banner to user.
   * If user accept it. MediaStream object will be available, otherwise catch block will execute
   * and we should show a message to user that you should let us access camera and/or microphone.
   * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/mediaDevices
   */
  async grantAccess() {
    const constraints: MediaStreamConstraints = {
      audio: { echoCancellation: true },
    };

    if (this.signalType === SignalingOffer.Type.VIDEO_CALLING) {
      constraints.video = {
        width: { min: 320 },
        height: { min: 240 },
        frameRate: { min: 15 },
      };
    }

    return navigator.mediaDevices.getUserMedia(constraints);
  }

  /**
   *
   * @param signalingType
   * @param user
   */
  async callUser(signalingType: SignalingOffer.AsObject["type"], user?: RegisteredUser.AsObject) {
    this.user = user;
    this.signalType = signalingType;
    this.peerConnection = this.createPeer();

    const stream = await this.grantAccess();
    store.dispatch(setUserStream(stream));
    stream?.getTracks().forEach((track) => this.peerConnection?.addTrack(track, stream));

    store.dispatch(setSignalingStatus(SIGNALING_STATUS.CALLING));
  }

  async onOffer(callerSdp: string, callerUserId: SignalingOfferResponse.AsObject["callerUserId"], signalingType: SignalingOffer.AsObject["type"]) {
    this.signalType = signalingType;
    if (callerSdp !== "") {
      this.callerUserId = callerUserId;
      this.callerSdp = callerSdp;
      await protoHandler(SIGNALING_RINGING, {});
    }
  }

  /**
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/setLocalDescription
   * https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/setRemoteDescription
   * @param calledSdp
   */
  async onAccept(calledSdp: string) {
    if (calledSdp !== "") {
      try {
        const desc: RTCSessionDescriptionInit = new RTCSessionDescription({ type: "answer", sdp: calledSdp });
        this.peerConnection?.setLocalDescription(this.sdp!);
        this.peerConnection?.setRemoteDescription(desc).then().catch(console.log);
      } catch (e) {}
    } else {
      // goToPopup(POPUP_CALL_VIDEO);
    }
  }

  async createAnswer() {
    try {
      this.peerConnection = this.createPeer();

      const desc: RTCSessionDescriptionInit = new RTCSessionDescription({ type: "offer", sdp: this.callerSdp });
      await this.peerConnection.setRemoteDescription(desc);

      const stream = await this.grantAccess();
      stream.getTracks().forEach((track) => this.peerConnection?.addTrack(track, stream));
      store.dispatch(setUserStream(stream));

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      await protoHandler(SIGNALING_ACCEPT, { calledSdp: answer.sdp });
    } catch (e) {}
  }

  onCandidate = (peerCandidate: string, peerSdpMLineIndex: number, peerSdpMId: string) => {
    const ICECandidate: RTCIceCandidateInit = new RTCIceCandidate({ candidate: peerCandidate, sdpMLineIndex: peerSdpMLineIndex, sdpMid: peerSdpMId });
    this.peerConnection?.addIceCandidate(ICECandidate).catch((e) => console.log(e));
  };

  createPeer() {
    const peer = new RTCPeerConnection(this.config);

    peer.onicecandidate = this.onICECandidate;
    peer.onnegotiationneeded = this.onNegotiationNeeded;
    peer.ontrack = this.onTrack;
    peer.oniceconnectionstatechange = (e) => this.onICEConnectionStateChange(e);

    return peer;
  }

  /**
   * this event occurs on adding track to peerConnection.
   * here we create offer and send it to servers.
   * https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/onnegotiationneeded
   */
  onNegotiationNeeded = async () => {
    try {
      const offer = await this.peerConnection!.createOffer();
      this.sdp = offer;
      await protoHandler(SIGNALING_OFFER, { type: this.signalType, calledUserId: this.user?.id, callerSdp: offer.sdp });
    } catch (e) {
      if (e instanceof ServerError) {
        switch (e.errorResponse.getMajorCode()) {
          case ERROR_SIGNALING_OFFER_FORBIDDEN:
            if (e.errorResponse.getMinorCode() === 9) {
              store.dispatch(setSignalingStatus(SIGNALING_STATUS.BUSY));
            } else {
              store.dispatch(setSignalingStatus(SIGNALING_STATUS.UNAVAILABLE));
            }
            break;
          case ERROR_SIGNALING_OFFER_BLOCKED_BY_PEER:
          case ERROR_SIGNALING_OFFER_PRIVACY_PROTECTION:
            store.dispatch(setSignalingStatus(SIGNALING_STATUS.UNAVAILABLE));
            break;
          default:
            store.dispatch(setSignalingStatus(SIGNALING_STATUS.DISCONNECTED));
        }
      } else {
        store.dispatch(setSignalingStatus(SIGNALING_STATUS.DISCONNECTED));
      }
    }
  };

  /**
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/onicecandidate
   * @param event
   */
  async onICECandidate(event: RTCPeerConnectionIceEvent) {
    try {
      if (event.candidate) {
        await protoHandler(SIGNALING_CANDIDATE, {
          candidate: event.candidate.candidate,
          sdpMId: event.candidate.sdpMid as string | undefined,
          sdpMLineIndex: event.candidate.sdpMLineIndex as number | undefined,
        });
      }
    } catch (e) {}
  }

  /**
   * ontrack event occurs when a track added to peerConnection. then we attach it stream to video srcObject.
   * https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/ontrack
   * @param event
   */
  onTrack(event: RTCTrackEvent) {
    store.dispatch(setPartnerStream(event.streams[0]));
  }

  /**
   *
   * @param event
   */
  onICEConnectionStateChange(event: Event) {
    // @ts-ignore
    switch (event.target?.iceConnectionState) {
      case "closed":
      case "disconnected":
      case "failed":
        store.dispatch(setSignalingStatus(SIGNALING_STATUS.DISCONNECTED));
        break;
      case "checking":
        store.dispatch(setSignalingStatus(SIGNALING_STATUS.CONNECTING));
        break;
      case "connected":
      case "completed":
        store.dispatch(setSignalingStatus(SIGNALING_STATUS.CONNECTED));
        break;
    }
  }

  /**
   *
   * @param iceServersList
   */
  setConfig(iceServersList: SignalingGetConfigurationResponse.AsObject["iceServerList"]) {
    const iceServers: RTCIceServer[] = [];

    iceServersList.forEach((item) => {
      iceServers.push({
        urls: item.url,
        credential: item.credential,
        username: item.username,
      });
    });

    this.config = {
      iceServers: iceServers,
    };
  }

  onHold(isSessionHold: SignalingStateType["sessionHold"]) {
    const userStream = store.getState().signaling.userStream;
    const partnerStream = store.getState().signaling.partnerStream;

    userStream?.getTracks().forEach((track) => (track.enabled = !isSessionHold));
    partnerStream?.getTracks().forEach((track) => (track.enabled = !isSessionHold));

    store.dispatch(setSignalingSessionHold(isSessionHold));
  }

  muteAudio(mute: boolean) {
    const userStream = store.getState().signaling.userStream;
    const partnerStream = store.getState().signaling.partnerStream;

    userStream?.getAudioTracks().forEach((track) => (track.enabled = !mute));
    partnerStream?.getAudioTracks().forEach((track) => (track.enabled = !mute));
  }

  close(isSendLeave: boolean) {
    store.dispatch(setSignalingStatus(SIGNALING_STATUS.DISCONNECTED));
    this.peerConnection?.close();
    store.dispatch(stopUserStream());
    store.dispatch(stopPartnerStream());
    if (isSendLeave) {
      protoHandler(SIGNALING_LEAVE, {}).then();
    }
  }
}
